import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  XAxis, YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import {
  SystemInsightsDataService,
  OrgInsights,
  InsightPeriod,
  INSIGHT_PERIOD_OPTIONS,
  formatCost,
  formatHours,
  lastActivityLabel,
} from '../../services/systemInsightsDataService';

// Two categorical hues only, in fixed order: the app's green, then its blue.
// Validated against the ivory chart surface for colour-vision separation
// (ΔE 21 deutan / 22.6 normal). The app's amber fails contrast on ivory, so it
// is not used as a series colour here — statuses keep their own vocabulary.
const SERIES_GREEN = '#26923f';
const SERIES_BLUE = '#3498db';
const AXIS = '#7e8a78';
const GRID = '#e6e9e4';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// The heatmap shows a nursery's day rather than 24 mostly-empty columns, but
// it must never hide a post: the window starts at 06:00–20:00 and stretches to
// cover anything written outside it (late write-ups are exactly the thing worth
// seeing).
const HEAT_MIN_HOUR = 6;
const HEAT_MAX_HOUR = 20;

interface OrgInsightsPanelProps {
  orgId: number;
}

/**
 * System Admin → Organisation → Activity. How much this organisation actually
 * uses the product: what gets written, published, photographed and reported,
 * how many staff and devices are involved, what the AI costs, and which
 * features are touched at all.
 *
 * Aggregates only — the endpoint behind it cannot return a child, a staff
 * member or a post, by construction (migration 050).
 */
const OrgInsightsPanel: React.FC<OrgInsightsPanelProps> = ({ orgId }) => {
  const [period, setPeriod] = useState<InsightPeriod>('this_week');
  const [data, setData] = useState<OrgInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    SystemInsightsDataService.fetchOrg(orgId, period)
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setError(err?.response?.data?.error || 'Failed to load activity.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, period]);

  const unit = data?.period.unit === 'month' ? 'month' : 'week';
  const periodLabel = (INSIGHT_PERIOD_OPTIONS.find(o => o.id === period)?.label ?? '').toLowerCase();

  const dailyData = useMemo(
    () => (data?.daily ?? []).map(d => ({
      // "Mon 14" reads faster than an ISO date on a crowded axis.
      label: `${WEEKDAYS[(new Date(`${d.day}T00:00:00`).getDay() + 6) % 7]} ${Number(d.day.slice(8))}`,
      posts: d.posts,
      published: d.published,
      photos: d.photos,
      reports: d.reports,
      attendance: d.attendance,
    })),
    [data],
  );

  const trendData = useMemo(
    () => (data?.trend ?? []).map(t => ({
      label: unit === 'month' ? t.start.slice(0, 7) : t.start.slice(5),
      posts: t.posts,
      photos: t.photos,
      published: t.published,
      isCurrent: t.isCurrent,
    })),
    [data, unit],
  );

  // Busiest cell sets the top of the sequential ramp; everything else is read
  // against it. One hue, light to dark — never a rainbow.
  const heat = useMemo(() => {
    const cells = new Map<string, number>();
    let max = 0;
    for (const c of data?.heatmap ?? []) {
      cells.set(`${c.weekday}-${c.hour}`, c.posts);
      if (c.posts > max) max = c.posts;
    }
    return { cells, max };
  }, [data]);

  const heatHours = useMemo(() => {
    const hours = (data?.heatmap ?? []).map(c => c.hour);
    const from = Math.min(HEAT_MIN_HOUR, ...hours);
    const to = Math.max(HEAT_MAX_HOUR, ...hours);
    return Array.from({ length: to - from + 1 }, (_, i) => i + from);
  }, [data]);

  const heatColour = (count: number): string => {
    if (count <= 0) return 'transparent';
    const ratio = heat.max > 0 ? count / heat.max : 0;
    // Five discrete steps: a continuous alpha is unreadable at this cell size.
    const step = Math.min(4, Math.floor(ratio * 5));
    return `rgba(38, 146, 63, ${[0.14, 0.3, 0.48, 0.68, 0.88][step]})`;
  };

  const coverage = (n: number): string => {
    const onRoll = data?.children.onRoll ?? 0;
    if (!onRoll) return '—';
    return `${Math.round((n / onRoll) * 100)}%`;
  };

  const staffEngagement = (): string => {
    const total = data?.staff.total ?? 0;
    if (!total) return '—';
    return `${Math.round(((data?.staff.postedInPeriod ?? 0) / total) * 100)}%`;
  };

  return (
    <div className="sys-page__body">
      <div className="sys-period mb-3" role="group" aria-label="Period">
        {INSIGHT_PERIOD_OPTIONS.map(opt => (
          <button
            key={opt.id}
            type="button"
            className="sys-period__btn"
            aria-pressed={period === opt.id}
            onClick={() => setPeriod(opt.id)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {error ? (
        <div className="alert alert-danger">{error}</div>
      ) : loading && !data ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : data ? (
        // Stale numbers under a new period label read as fact — dim them
        // until the new ones land.
        <div className={`sys-insights${loading ? ' sys-stale' : ''}`} aria-busy={loading}>
          {/* ── Headline ─────────────────────────────────────────────── */}
          <div className="sys-stats">
            <Stat label="Posts" value={data.posts.period.toLocaleString()} icon="fa-pen-to-square"
              sub={`avg ${data.posts.average} per ${unit}`} />
            <Stat label="Published" value={data.publishing.period.toLocaleString()} icon="fa-paper-plane"
              sub={`avg ${data.publishing.average} per ${unit}`} />
            <Stat label="Photos" value={data.photos.period.toLocaleString()} icon="fa-image"
              sub={`avg ${data.photos.average} per ${unit}`} />
            <Stat label="Reports" value={data.reports.period.toLocaleString()} icon="fa-file-lines"
              sub={`avg ${data.reports.average} per ${unit}`} />
            <Stat label="AI spend" value={formatCost(data.ai.cost, data.ai.currency)} icon="fa-robot"
              sub={`avg ${formatCost(data.ai.costAverage, data.ai.currency)} per ${unit}`} />
            <Stat label="Last activity" value={lastActivityLabel(data.lastActivityAt)} icon="fa-clock"
              sub="Something created, not a login" />
          </div>

          {/* ── Day by day ───────────────────────────────────────────── */}
          <Panel title="Day by day" icon="fa-chart-column"
            note={`Posts written and posts published, ${periodLabel}. UK days${period.startsWith('this_') ? '; today is the last bar' : ''}.`}>
            {dailyData.length === 0 ? (
              <Empty>Nothing recorded yet this period.</Empty>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={dailyData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                  <XAxis dataKey="label" stroke={AXIS} fontSize={11} tickLine={false} />
                  <YAxis stroke={AXIS} fontSize={11} tickLine={false} allowDecimals={false} width={36} />
                  <Tooltip cursor={{ fill: 'rgba(38,146,63,0.06)' }} />
                  <Legend />
                  <Bar dataKey="posts" name="Written" fill={SERIES_GREEN} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="published" name="Published" fill={SERIES_BLUE} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>

          {/* ── Trend behind the average ─────────────────────────────── */}
          <Panel title={`${unit === 'month' ? 'Month' : 'Week'} by ${unit}`} icon="fa-chart-line"
            note={`The ${data.period.averagePeriods} complete ${unit}s the average is taken from, plus the one on screen. The dashed line is that average.`}>
            {trendData.length === 0 ? (
              <Empty>No history yet.</Empty>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                  <XAxis dataKey="label" stroke={AXIS} fontSize={11} tickLine={false} />
                  <YAxis stroke={AXIS} fontSize={11} tickLine={false} allowDecimals={false} width={36} />
                  <Tooltip />
                  <Legend />
                  <ReferenceLine y={data.posts.average} stroke={AXIS} strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="posts" name="Written" stroke={SERIES_GREEN} strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="published" name="Published" stroke={SERIES_BLUE} strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Panel>

          {/* ── When people write ────────────────────────────────────── */}
          <Panel title="When posts are written" icon="fa-table-cells"
            note={`UK local time, ${String(heatHours[0]).padStart(2, '0')}:00–${String(heatHours[heatHours.length - 1]).padStart(2, '0')}:00. Darker means busier; the busiest hour in the period sets the scale.`}>
            {heat.max === 0 ? (
              <Empty>No posts written in this period.</Empty>
            ) : (
              <div className="sys-heat" role="img"
                aria-label={`Posts by weekday and hour. Busiest hour had ${heat.max} posts.`}>
                <div className="sys-heat__row sys-heat__row--head">
                  <span className="sys-heat__day" />
                  {heatHours.map(h => (
                    <span key={h} className="sys-heat__hour">{h % 3 === 0 ? h : ''}</span>
                  ))}
                </div>
                {WEEKDAYS.map((label, i) => (
                  <div className="sys-heat__row" key={label}>
                    <span className="sys-heat__day">{label}</span>
                    {heatHours.map(h => {
                      const count = heat.cells.get(`${i + 1}-${h}`) ?? 0;
                      return (
                        <span
                          key={h}
                          className="sys-heat__cell"
                          style={{ background: heatColour(count) }}
                          title={`${label} ${String(h).padStart(2, '0')}:00 — ${count} post${count === 1 ? '' : 's'}`}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </Panel>

          {/* ── Children & coverage ──────────────────────────────────── */}
          <div className="sys-insights__pair">
            <Panel title="Children" icon="fa-child-reaching">
              <Field label="On roll" value={data.children.onRoll.toLocaleString()} />
              <Field label="Of which inactive" value={data.children.archived.toLocaleString()}
                note="Switched off, still counted" />
              <Field label="Leavers" value={data.children.leavers.toLocaleString()}
                note="Kept for retention, never billed" />
              <Field label="Posted about, last 14 days" value={`${data.children.covered14d} (${coverage(data.children.covered14d)})`} />
              <Field label="Posted about, last 28 days" value={`${data.children.covered28d} (${coverage(data.children.covered28d)})`} />
              {data.children.pendingDeletion > 0 && (
                <Field label="Awaiting permanent deletion" value={data.children.pendingDeletion.toLocaleString()}
                  warn={data.children.overdueDeletion > 0}
                  note={data.children.overdueDeletion > 0 ? `${data.children.overdueDeletion} past due` : undefined} />
              )}
            </Panel>

            <Panel title="Staff" icon="fa-user-group"
              note="A person holding two roles is counted under both, so the roles need not add up to the total.">
              <Field label="Total" value={data.staff.total.toLocaleString()} />
              <Field label="Admin" value={data.staff.admin.toLocaleString()} />
              <Field label="Editor" value={data.staff.editor.toLocaleString()} />
              <Field label="Reporting" value={data.staff.reporting.toLocaleString()} />
              {data.staff.noRole > 0 && <Field label="No role" value={data.staff.noRole.toLocaleString()} />}
              <Field label="External" value={data.staff.external.toLocaleString()} note="Parents and other outside parties" />
              <Field label="Disabled" value={data.staff.disabled.toLocaleString()} />
              <Field label="Wrote something this period" value={`${data.staff.postedInPeriod} (${staffEngagement()})`} />
              <Field label="Devices used" value={data.posts.devices.toLocaleString()}
                note="Distinct device logins that created anything" />
            </Panel>
          </div>

          {/* ── Publishing & photos ──────────────────────────────────── */}
          <div className="sys-insights__pair">
            <Panel title="Publishing" icon="fa-paper-plane">
              <Field label="Published this period" value={data.publishing.period.toLocaleString()} />
              <Field label="Typical time to publish" value={formatHours(data.publishing.lagMedianHours)}
                note="Median, from writing to publishing" />
              <Field label="Awaiting review" value={data.publishing.backlog.toLocaleString()}
                warn={(data.publishing.backlogOldestDays ?? 0) >= 21}
                note={data.publishing.backlogOldestDays != null
                  ? `Oldest ${data.publishing.backlogOldestDays} days`
                  : undefined} />
              <Field label="Typical write-up delay" value={formatHours(data.posts.entryLagMedianHours)}
                note="Median, from the observation to the post" />
              <Field label="Posts, all time" value={data.posts.total.toLocaleString()} />
            </Panel>

            <Panel title="Photos" icon="fa-image">
              <Field label="Uploaded this period" value={data.photos.period.toLocaleString()} />
              <Field label="Face-blurred this period" value={data.photos.blurred.toLocaleString()} />
              <Field label="Photos, all time" value={data.photos.total.toLocaleString()} />
              <p className="sys-field__foot">
                File sizes are not recorded yet, so storage totals aren’t available here.
              </p>
            </Panel>
          </div>

          {/* ── Reports & AI ─────────────────────────────────────────── */}
          <div className="sys-insights__pair">
            <Panel title="Reports" icon="fa-file-lines">
              <Field label="Generated this period" value={data.reports.period.toLocaleString()} />
              <Field label="Reports, all time" value={data.reports.total.toLocaleString()} />
              {data.reports.templates.length > 0 && (
                <table className="sys-table sys-table--inline">
                  <thead><tr><th>Template</th><th className="sys-table__num">Reports</th></tr></thead>
                  <tbody>
                    {data.reports.templates.map(t => (
                      <tr key={t.name}>
                        <td>{t.name}</td>
                        <td className="sys-table__num">{t.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>

            <Panel title="AI spend" icon="fa-robot">
              <Field label="Enrichment" value={formatCost(data.ai.enrichment, data.ai.currency)} />
              <Field label="Typo correction" value={formatCost(data.ai.typoCorrection, data.ai.currency)} />
              <Field label="Embedding" value={formatCost(data.ai.embedding, data.ai.currency)} />
              <Field label="Total this period" value={formatCost(data.ai.cost, data.ai.currency)} />
              <Field label="Cost per post" value={data.posts.period > 0
                ? formatCost(data.ai.cost / data.posts.period, data.ai.currency)
                : '—'} />
              <Field label="Calls" value={data.ai.calls.toLocaleString()} />
              <Field label="Failed calls" value={data.ai.failures.toLocaleString()} warn={data.ai.failures > 0} />
            </Panel>
          </div>

          {/* ── Features & support ───────────────────────────────────── */}
          <div className="sys-insights__pair">
            <Panel title="Feature use" icon="fa-puzzle-piece" note="Counts for the selected period.">
              <Field label="Attendance entries" value={data.features.attendance.toLocaleString()} />
              <Field label="Child documents added or changed" value={data.features.childDocuments.toLocaleString()} />
              <Field label="Organisation documents added or changed" value={data.features.orgDocuments.toLocaleString()} />
              <Field label="Emails sent" value={data.features.emails.toLocaleString()} />
            </Panel>

            <Panel title="Support signals" icon="fa-life-ring">
              <Field label="PIN resets and requests" value={data.support.pinEvents.toLocaleString()} />
              <Field label="Locked out right now" value={data.support.pinLockouts.toLocaleString()}
                warn={data.support.pinLockouts > 0} />
              <Field label="Email bounces" value={data.support.emailBounces.toLocaleString()}
                warn={data.support.emailBounces > 0} />
              <Field label="Email complaints" value={data.support.emailComplaints.toLocaleString()}
                warn={data.support.emailComplaints > 0} />
            </Panel>
          </div>

          <p className="sys-page__footnote">
            Counts only — this tab cannot show a child, a staff member or a post. Weeks run Monday to Sunday, UK time.
            “Last activity” is the last thing anyone created; opening the app without writing anything is not recorded.
          </p>
        </div>
      ) : null}
    </div>
  );
};

const Panel: React.FC<{ title: string; icon: string; note?: string; children: React.ReactNode }> =
  ({ title, icon, note, children }) => (
    <div className="ivory-panel sys-panel sys-panel--pad">
      <h2 className="sys-panel__title"><i className={`fas ${icon}`} aria-hidden="true" />{title}</h2>
      {note && <p className="sys-panel__note">{note}</p>}
      {children}
    </div>
  );

const Field: React.FC<{ label: string; value: string; note?: string; warn?: boolean }> =
  ({ label, value, note, warn }) => (
    <div className="sys-field">
      <span className="sys-field__label">{label}</span>
      <span className={`sys-field__value ${warn ? 'sys-state sys-state--warn' : ''}`}>
        {warn && <i className="fas fa-triangle-exclamation" aria-hidden="true" />}
        {value}
        {note && <span className="sys-field__note">{note}</span>}
      </span>
    </div>
  );

const Stat: React.FC<{ label: string; value: string; icon: string; sub?: string }> = ({ label, value, icon, sub }) => (
  <div className="ivory-panel sys-stat">
    <div className="sys-stat__label">
      <i className={`fas ${icon}`} aria-hidden="true" />
      {label}
    </div>
    <div className="sys-stat__value">{value}</div>
    {sub && <div className="sys-stat__sub">{sub}</div>}
  </div>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="sys-table__empty m-0">{children}</p>
);

export default OrgInsightsPanel;
